import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight, FaPrint, FaTimes } from 'react-icons/fa';
import Toolbar, { ToolbarButtonConfig, ToolbarFilterConfig } from '../Toolbar/Toolbar';
import './PrintableView.scss';

export interface PrintablePage {
    /** Label shown in the toolbar page-indicator when navigating (e.g. "Factuur", "Urenspecificatie") */
    label: string;
    /** Content rendered inside the A4 paper shell */
    content: React.ReactNode;
    /** Optional extra CSS class applied to the paper wrapper div */
    className?: string;
}

interface PrintableViewProps {
    open: boolean;
    /** Title displayed on the left side of the toolbar. Defaults to the translated "Preview" string. */
    title?: string;
    onClose: () => void;
    /** Each element represents one A4 page. */
    pages: PrintablePage[];
    /**
     * When this value changes the view resets to page 0.
     * Useful when the parent switches to a different document while the viewer stays open.
     */
    resetKey?: string | number;
}

const PrintableView: React.FC<PrintableViewProps> = ({
    open,
    title,
    onClose,
    pages,
    resetKey,
}) => {
    const { t } = useTranslation('common');
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        setCurrentPage(0);
    }, [resetKey]);

    if (!open || pages.length === 0) return null;

    const totalPages = pages.length;

    // ── Toolbar config ──────────────────────────────────────────────────────────

    const toolbarFilters: ToolbarFilterConfig[] = [
        {
            key: 'title',
            type: 'custom',
            render: () => <span className="pv__toolbar-title">{title ?? t('printableView.preview')}</span>,
        },
    ];

    if (totalPages > 1) {
        toolbarFilters.push({
            key: 'nav',
            type: 'custom',
            render: () => (
                <div className="pv__toolbar-nav">
                    <button
                        type="button"
                        className="toolbar__btn toolbar__btn--secondary"
                        onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                        disabled={currentPage === 0}
                    >
                        <FaChevronLeft />
                    </button>
                    <span className="pv__toolbar-page-indicator">
                        {pages[currentPage].label} {t('printableView.pageOf', { current: currentPage + 1, total: totalPages })}
                    </span>
                    <button
                        type="button"
                        className="toolbar__btn toolbar__btn--secondary"
                        onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={currentPage === totalPages - 1}
                    >
                        <FaChevronRight />
                    </button>
                </div>
            ),
        });
    }

    const toolbarButtons: ToolbarButtonConfig[] = [
        {
            key: 'print',
            icon: <FaPrint />,
            label: t('print'),
            onClick: () => window.print(),
            variant: 'primary',
        },
        {
            key: 'close',
            icon: <FaTimes />,
            label: t('close'),
            onClick: onClose,
            variant: 'secondary',
        },
    ];

    // ───────────────────────────────────────────────────────────────────────────

    const content = (
        <div className="pv__backdrop">

            {/* ── Toolbar (screen only) ── */}
            <Toolbar filters={toolbarFilters} buttons={toolbarButtons} />

            {/* ── Scrollable area around the A4 sheets ── */}
            <div className="pv__scroll-area">
                {pages.map((page, idx) => (
                    <div
                        key={idx}
                        className={[
                            'pv__paper',
                            currentPage !== idx ? 'pv__paper--inactive' : '',
                            page.className ?? '',
                        ].filter(Boolean).join(' ')}
                    >
                        {page.content}
                    </div>
                ))}
            </div>

        </div>
    );

    return ReactDOM.createPortal(content, document.body);
};

export default PrintableView;

